import config from '../../config';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { proxyEmailChange, vansiteMailSyncContact } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { oneSignalAddTags } from '../../util/mobile/oneSignal';

// ================ Action types ================ //

export const SAVE_EMAIL_REQUEST = 'app/ContactSettingsPage/SAVE_EMAIL_REQUEST';
export const SAVE_EMAIL_SUCCESS = 'app/ContactSettingsPage/SAVE_EMAIL_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/ContactSettingsPage/SAVE_EMAIL_ERROR';

export const SAVE_PHONE_NUMBER_REQUEST = 'app/ContactSettingsPage/SAVE_PHONE_NUMBER_REQUEST';
export const SAVE_PHONE_NUMBER_SUCCESS = 'app/ContactSettingsPage/SAVE_PHONE_NUMBER_SUCCESS';
export const SAVE_PHONE_NUMBER_ERROR = 'app/ContactSettingsPage/SAVE_PHONE_NUMBER_ERROR';

export const RESET_PASSWORD_REQUEST = 'app/ContactSettingsPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/ContactSettingsPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/ContactSettingsPage/RESET_PASSWORD_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.ContactSettingsPage} */
const initialState = {
  saveEmailInProgress: false,
  saveEmailError: null,
  savePhoneNumberInProgress: false,
  savePhoneNumberError: null,
  resetPasswordInProgress: false,
  resetPasswordError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_EMAIL_REQUEST:
      return { ...state, saveEmailInProgress: true, saveEmailError: null };
    case SAVE_EMAIL_SUCCESS:
      return { ...state, saveEmailInProgress: false };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveEmailInProgress: false, saveEmailError: payload };

    case SAVE_PHONE_NUMBER_REQUEST:
      return { ...state, savePhoneNumberInProgress: true, savePhoneNumberError: null };
    case SAVE_PHONE_NUMBER_SUCCESS:
      return { ...state, savePhoneNumberInProgress: false };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, savePhoneNumberInProgress: false, savePhoneNumberError: payload };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveEmailRequest = () => ({ type: SAVE_EMAIL_REQUEST });
export const saveEmailSuccess = () => ({ type: SAVE_EMAIL_SUCCESS });
export const saveEmailError = (e) => ({ type: SAVE_EMAIL_ERROR, error: true, payload: e });

export const savePhoneNumberRequest = () => ({ type: SAVE_PHONE_NUMBER_REQUEST });
export const savePhoneNumberSuccess = () => ({ type: SAVE_PHONE_NUMBER_SUCCESS });
export const savePhoneNumberError = (e) => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  error: true,
  payload: e,
});

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });
export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });
export const resetPasswordError = (e) => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Make a phone number update request to the API and return the current user.
 */
export const savePhoneNumber = (params) => async (dispatch, getState, sdk) => {
  dispatch(savePhoneNumberRequest());
  const { phoneNumber, phoneNumberVerification } = params;

  try {
    const response = await sdk.currentUser.updateProfile(
      {
        protectedData: { phoneNumber },
        privateData: { phoneNumberVerification },
      },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      },
    );
    const entities = denormalisedResponseEntities(response);
    const user = entities[0];
    dispatch(savePhoneNumberSuccess());
    dispatch(currentUserShowSuccess(user));
  } catch (e) {
    dispatch(savePhoneNumberError(storableError(e)));
    logError(e, 'phone-number-change-failed', { phoneNumber });
  }
};

/**
 * Make a email update request to the API and return the current user.
 */
export const saveEmail = (params) => async (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;
  // Vor dem Wechsel merken: vansite-mail adressiert Kontakte über die E-Mail, der
  // Kontakt zur alten Adresse muss danach entfernt werden.
  const previousEmail = getState().user.currentUser?.attributes?.email;

  dispatch(saveEmailRequest());
  try {
    const userQueryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };
    let response;
    if (config.proxyLoginEnabled) {
      // marketplace-web@P1: E-Mail-Änderung über das BFF (dual-write), danach den
      // aktualisierten User nachladen — das BFF liefert bewusst nur { ok: true }.
      await proxyEmailChange({ newEmail: email, currentPassword });
      response = await sdk.currentUser.show(userQueryParams);
    } else {
      response = await sdk.currentUser.changeEmail({ email, currentPassword }, userQueryParams);
    }
    const entities = denormalisedResponseEntities(response);
    const user = entities[0];
    dispatch(saveEmailSuccess());
    dispatch(currentUserShowSuccess(user));
    oneSignalAddTags({ email: email });
    // Best-effort: der E-Mail-Wechsel selbst ist bereits durch, ein Fehler beim
    // Spiegeln darf ihn nicht als fehlgeschlagen erscheinen lassen.
    vansiteMailSyncContact({ previousEmail }).catch((e) =>
      logError(e, 'vansite-mail-sync-contact-failed', { email }),
    );
  } catch (e) {
    dispatch(saveEmailError(storableError(e)));
    logError(e, 'email-change-failed', { email });
  }
};

export const resetPassword = (email) => async (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());

  try {
    await sdk.passwordReset.request({ email });
    dispatch(resetPasswordSuccess());
  } catch (e) {
    dispatch(resetPasswordError(storableError(e)));
    logError(e, 'password-reset-failed', { email });
  }
};

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
